// app/src/lib/scale/attribution.js — acquisition / campaign (UTM) capture.
//
// Flow: a QR / campaign link (e.g. https://qr.getkanjo.com/c/TEST-a766 →
// app?c=TEST-a766) lands the user on the app. We capture the campaign code +
// any UTM params FIRST-TOUCH into localStorage (survives the pre-signup bounce
// through the hosted UI), then sync it to the user's plan-state once they're
// authenticated. The finix-webhook later stamps this channel onto subscription
// ledger rows, so the admin dashboard can break subscribers down by campaign.

import { getAccessToken } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';

const KEY = 'kanjo.attribution';
const SYNC_KEY = 'kanjo.attribution.synced';
const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

// Read params from BOTH the query string and the hash-route query (the app is
// hash-routed, so a link may carry the code in either place).
function parseParams() {
  const out = {};
  let search, hashParams;
  try { search = new URLSearchParams(window.location.search || ''); } catch { search = new URLSearchParams(); }
  const hash = window.location.hash || '';
  const qi = hash.indexOf('?');
  try { hashParams = new URLSearchParams(qi >= 0 ? hash.slice(qi + 1) : ''); } catch { hashParams = new URLSearchParams(); }
  const get = (k) => search.get(k) || hashParams.get(k);

  const c = get('c') || get('campaign');
  if (c) out.channel = c;
  for (const u of UTM) { const v = get(u); if (v) out[u] = v; }
  // Fall back to utm_campaign as the channel label if no short code.
  if (!out.channel && out.utm_campaign) out.channel = out.utm_campaign;
  return out;
}

/** Capture FIRST-TOUCH attribution from the URL into localStorage. Idempotent. */
export function captureAttribution() {
  try {
    const params = parseParams();
    if (!Object.keys(params).length) return getAttribution(); // nothing in URL
    if (getAttribution()) return getAttribution();            // first-touch wins
    const rec = {
      ...params,
      capturedAt: new Date().toISOString(),
      landing: (window.location.pathname || '') + (window.location.hash || ''),
    };
    localStorage.setItem(KEY, JSON.stringify(rec));
    localStorage.removeItem(SYNC_KEY);
    return rec;
  } catch { return null; }
}

export function getAttribution() {
  try { const v = localStorage.getItem(KEY); return v ? JSON.parse(v) : null; }
  catch { return null; }
}

/** Send captured attribution to the server (stamps plan-state, first-touch). */
export async function syncAttribution() {
  try {
    const rec = getAttribution();
    if (!rec || localStorage.getItem(SYNC_KEY)) return;
    const at = await getAccessToken();
    if (!at) return; // not signed in yet — retry on a later call
    const cfg = getConfig();
    const res = await fetch(`${cfg.apiBaseUrl}/me/attribution`, {
      method: 'POST',
      headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
      body: JSON.stringify(rec),
    });
    if (res.ok) localStorage.setItem(SYNC_KEY, '1');
  } catch { /* best-effort; will retry next load */ }
}
